import { getBackendUrl } from '../utils/utils';

export type AssetType = 'avatar' | 'animation' | 'background_image' | 'background_3d' | 'furniture' | 'voice_sample';

export interface SceneAsset {
  id: string;
  organization_id: string;
  asset_type: AssetType;
  name: string;
  filename: string;
  content_type: string;
  file_size: number;
  metadata?: Record<string, any>;
  is_default: boolean;
  signed_url?: string;
  uploaded_by: string;
  created_at: string;
  updated_at?: string;
}

/**
 * List assets for the user's org, optionally filtered by type.
 * Includes system defaults visible to all orgs.
 * Super admins may pass orgId to scope results to a specific org + platform defaults.
 * Without orgId, super admins in platform mode see only platform-wide assets.
 */
export async function listAssets(
  token: string,
  assetType?: AssetType,
  orgId?: string,
): Promise<SceneAsset[]> {
  const params = new URLSearchParams();
  if (assetType) params.set('asset_type', assetType);
  if (orgId) params.set('org_id', orgId);
  const qs = params.toString();
  const url = getBackendUrl(`/scene-assets/${qs ? `?${qs}` : ''}`);

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to list assets: ${response.status}`);
  }

  return response.json();
}

/**
 * Upload a new asset to GCS via the backend.
 * Returns the created asset record with a signed URL.
 */
export async function uploadAsset(
  token: string,
  file: File,
  assetType: AssetType,
  name: string,
  metadata?: Record<string, any>,
  onProgress?: (percent: number) => void,
  organizationId?: string,
): Promise<SceneAsset> {
  const url = getBackendUrl('/scene-assets/upload');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('asset_type', assetType);
  formData.append('name', name);
  if (metadata) {
    formData.append('metadata_json', JSON.stringify(metadata));
  }
  if (organizationId) {
    formData.append('organization_id', organizationId);
  }

  // Use XMLHttpRequest for upload progress if callback provided
  if (onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.send(formData);
    });
  }

  // Simple fetch for uploads without progress tracking
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Upload failed: ${response.status} ${detail}`);
  }

  return response.json();
}

/**
 * Update asset metadata (name, metadata).
 */
export async function updateAsset(
  token: string,
  assetId: string,
  updates: { name?: string; metadata?: Record<string, any> },
): Promise<SceneAsset> {
  const url = getBackendUrl(`/scene-assets/${assetId}`);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update asset: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete an asset from GCS and the database.
 */
export async function deleteAsset(token: string, assetId: string): Promise<void> {
  const url = getBackendUrl(`/scene-assets/${assetId}`);
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete asset: ${response.status}`);
  }
}

export interface SceneAssetUsageEntry {
  scene_id: string;
  organization_id: string | null;
  prompt_id: string | null;
  name: string;
}

export interface SceneAssetUsage {
  in_use: boolean;
  scenes: SceneAssetUsageEntry[];
}

/**
 * Which scenes still directly reference this asset (a seat's avatar/table/chair, shared
 * furniture, an animation, or a background) — independent of whether any *character* also
 * uses it. Scene layouts store asset IDs in a JSONB blob, not a real foreign key, so
 * deleting an asset a scene still points at doesn't fail — that scene just silently loses
 * the piece next time it renders. Check this before deleting so that risk is visible.
 */
export async function getAssetUsage(token: string, assetId: string): Promise<SceneAssetUsage> {
  const url = getBackendUrl(`/scene-assets/${assetId}/usage`);
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to check asset usage: ${response.status}`);
  }

  return response.json();
}

/**
 * Batch-resolve asset IDs to signed URLs.
 * Used to hydrate a scene config's asset references with usable URLs.
 */
export async function getBatchUrls(
  token: string,
  assetIds: string[],
): Promise<Record<string, string>> {
  if (assetIds.length === 0) return {};

  const url = getBackendUrl('/scene-assets/batch-urls');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ asset_ids: assetIds }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get batch URLs: ${response.status}`);
  }

  const data = await response.json();
  return data.urls;
}
